import { DotsVertical, InfoCircle } from '@untitled-ui/icons-react/build/cjs';
import React from 'react';
import { useTranslation } from 'react-i18next';

import ActionTooltip from '../ActionTooltip';

interface IOption {
    label: string;
    value: string;
}

interface IProps {
    isError?: boolean;
    handleActionType: (value: string) => () => void;
    options: IOption[];
    children?: React.ReactNode;
    headerContent?: React.ReactNode;
    headerContentClassName?: string;
    isLoading?: boolean;
    rightAlignOffset?: boolean;
    tooltipContentFitWidth?: boolean;
    showOptions?: boolean;
}

const CardWithOptionsSkeleton = () => (
    <div className="rounded-lg border border-utilityGray200 bg-white shadow-xs px-3 py-4 gap-x-2 w-full animate-pulse">
        <div className="flex items-start justify-between gap-x-3 w-full">
            <div className="flex-1">
                <div className="h-5 bg-gray-200 rounded w-1/3 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
            <div className="w-4 h-4 bg-gray-200 rounded"></div>
        </div>
    </div>
);
const deleteOptionClassArray = ['delete_id', 'delete_contact', 'delete_primary_contact', 'delete_shipper_contact', 'delete_consignee_contact', 'delete_broker'];

const CardWithOptions = ({
    options,
    children,
    handleActionType,
    headerContent,
    headerContentClassName,
    isLoading = false,
    isError = false,
    rightAlignOffset = false,
    tooltipContentFitWidth = false,
    showOptions = true,
}: IProps) => {
    const { t } = useTranslation();

    if (isLoading) {
        return <CardWithOptionsSkeleton />;
    }

    return (
        <div className={`rounded-lg border ${isError ? 'border-red-600' : 'border-utilityGray200'} bg-white shadow-xs p-3 gap-x-2 w-full`}>
            <div className="flex items-start justify-between gap-x-3 w-full">
                {headerContent && <div className={`${headerContentClassName}`}>{headerContent}</div>}
                {showOptions ? (
                    <ActionTooltip
                        message={
                            <>
                                <ul className={`action-tooltip-select ${tooltipContentFitWidth ? 'min-w-fit' : 'min-w-[240px]'}`}>
                                    {options.map((option) => (
                                        <li key={option.value} className="action-tooltip-select-item" onClick={handleActionType(option.value)}>
                                            <p className={`action-tooltip-select-item-title ${deleteOptionClassArray.includes(option.value) ? 'text-fgErrorPrimary' : ''}`}>{option.label}</p>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        }
                        type="light"
                        tooltipPosition={rightAlignOffset ? 'right' : 'left'}
                        {...(rightAlignOffset ? { rightAlignOffset: 'translateX(-93%)' } : { leftAlignOffset: '!translate-x-0' })}
                        parentClassName="action-tooltip-select-wrap"
                        actionTooltipCustomClass="!w-[10px] action-tooltip-without-arrow"
                    >
                        <DotsVertical className="w-4 h-4 text-gray400" />
                    </ActionTooltip>
                ) : (
                    <ActionTooltip
                        message={t('Common.address_info')}
                        isOpenChildTopPosOnTop={true}
                        tooltipPosition="center"
                        parentClassName="min-w-[240px] max-w-[240px]"
                        openOnHover={true}
                        type="light"
                    >
                        <InfoCircle id="info-circle" className="h-4 w-4 text-primary cursor-pointer flex-shrink-0" />
                    </ActionTooltip>
                )}
            </div>
            {children && <div className="flex-1">{children}</div>}
        </div>
    );
};

export default CardWithOptions;
